/**
 * Re-read Central's switch-context IN THE BACKGROUND (BH-07).
 *
 * WHY: entitlements, plan and permissions arrive in switch-context, which
 * BootGate reads ONCE per boot. When Central pushed `entitlements.changed` /
 * `subscription.status.changed`, or the user came back from paying
 * (`?billing=success`), BeHeard only invalidated its own RTK reads — the
 * switch-context answer (scopes, capabilities, permissions, apps) and the token
 * minted with it stayed stale until a full reload. An upgrade did not unlock
 * anything; a downgrade did not lock anything.
 *
 * HOW: the same request BootGate makes, applied with `updateBootContext`, which
 * the shared boot slice provides exactly for this ("background refresh after
 * SSE events") — it replaces the context WITHOUT changing bootStatus, so the
 * app is not unmounted behind a "Verifying workspace…" spinner. (A `resetBoot`
 * would re-run boot but blank the whole tree — an in-progress practice or
 * assessment would be torn down by a billing event.)
 *
 * FAILURES use the shared classifier, with the boot verdicts where Central
 * actually refused (dead session → login, closed/denied → the refusal overlay),
 * a silent renewal for an aged-out token, and NOTHING for an outage: a failed
 * background refresh must not blank an app that is working.
 *
 * Single-flight: overlapping triggers share one request.
 */

import { classifyCentralFailure } from './centralFailureContract';
import { reportCentralFailure } from './reportCentralFailure';
import { applyBootFailure, applyTestingModeOverrides } from '../components/routing/BootGate';
import { updateCentralAccessToken } from '../store/slices/centralAuthSlice';
import { updateBootContext } from '../store/slices/bootContextSlice';
import { invalidateForCentralEvent } from '../store/api/centralEventInvalidation';
import { APP_ID, CENTRAL_URL } from '../config/centralAuth';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Dispatch = (action: any) => unknown;

export type RefreshOutcome = 'updated' | 'refused' | 'renewing' | 'failed' | 'skipped';

export interface RefreshSwitchContextDeps {
  dispatch: Dispatch;
  /** The live bearer (read at call time, so a rotation is honoured). */
  getToken: () => string | null;
  fetchImpl?: typeof fetch;
  centralUrl?: string;
}

/** Central events whose effect lives in switch-context, not only in BeHeard reads. */
export const SWITCH_CONTEXT_EVENTS: readonly string[] = Object.freeze([
  'entitlements.changed.v1',
  'subscription.status.changed.v1',
]);

let inFlight: Promise<RefreshOutcome> | null = null;

/** Test seam. */
export function __resetRefreshSwitchContext(): void {
  inFlight = null;
}

export function refreshSwitchContext(deps: RefreshSwitchContextDeps): Promise<RefreshOutcome> {
  if (inFlight) return inFlight;
  inFlight = run(deps).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function run(deps: RefreshSwitchContextDeps): Promise<RefreshOutcome> {
  const { dispatch } = deps;
  const token = deps.getToken();
  const centralUrl = deps.centralUrl ?? CENTRAL_URL;
  if (!token || !centralUrl) return 'skipped';
  const doFetch = deps.fetchImpl ?? fetch;

  // BeHeard's own reads derived from access (capabilities, me, dashboard …)
  // refetch too — the same tags the event mapping names.
  const invalidateReads = () => invalidateForCentralEvent(dispatch as never, 'entitlements.changed.v1', null, null);

  let response: Response;
  try {
    response = await doFetch(`${centralUrl}/api/suite/switch-context`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, 'X-App-Id': APP_ID },
    });
  } catch (err) {
    console.warn('[refreshSwitchContext] Central unreachable — keeping the current context:', err);
    invalidateReads();
    return 'failed';
  }

  if (response.ok) {
    const data = (await response.json().catch(() => null)) as Record<string, unknown> | null;
    if (!data || typeof data !== 'object' || !data.workspace) {
      console.warn('[refreshSwitchContext] unusable switch-context body — keeping the current context');
      invalidateReads();
      return 'failed';
    }
    if (typeof data.accessToken === 'string' && data.accessToken) {
      // Same hand-off BootGate makes (it passes Central's expiresIn through as-is).
      dispatch(updateCentralAccessToken({ accessToken: data.accessToken, expiresIn: data.expiresIn as number }));
    }
    dispatch(
      updateBootContext({
        currentWorkspace: applyTestingModeOverrides(data.workspace as Record<string, unknown>) as never,
        otherWorkspaces: (data.otherWorkspaces ?? []) as never,
        apps: (data.apps ?? []) as never,
        user: (data.user ?? null) as never,
        meta: data.meta as never,
      }),
    );
    invalidateReads();
    return 'updated';
  }

  const body = (await response.json().catch(() => undefined)) as unknown;
  const decision = classifyCentralFailure({ status: response.status, body });
  switch (decision.action) {
    case 'CENTRAL_LOGIN':
    case 'WORKSPACE_CLOSED':
    case 'WORKSPACE_PICKER':
    case 'APP_ACCESS_DENIED':
      applyBootFailure(response.status, body, dispatch as never);
      return 'refused';
    case 'RETRY_AFTER_REFRESH':
      // An aged-out token: renew silently; the next trigger reads with it.
      reportCentralFailure(dispatch, { status: response.status, body, source: 'refreshSwitchContext' });
      return 'renewing';
    default:
      if (response.status === 403) {
        applyBootFailure(response.status, body, dispatch as never);
        return 'refused';
      }
      console.warn('[refreshSwitchContext] keeping the current context:', decision.reason);
      invalidateReads();
      return 'failed';
  }
}
